import React, { useEffect, useState } from "react";
import { ShieldCheck, Clock, Wrench, Info } from "lucide-react";
import CONFIG from "../../config";

const DEFAULT_CHARGES = [
  { condition: "before_accept", label: "Before partner accepts",             percent: 5 },
  { condition: "after_accept",  label: "After partner accepts, before work", percent: 10 },
  { condition: "after_start",   label: "After work has started",             percent: 15 },
];

const TIER_ICON = { before_accept: ShieldCheck, after_accept: Clock, after_start: Wrench };

const CancellationPolicy = () => {
  const [charges, setCharges] = useState(DEFAULT_CHARGES);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${CONFIG.API_BASE_URL}/api/order/config/`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        if (Array.isArray(data.charges) && data.charges.length) setCharges(data.charges);
        if (data.updated_at) setUpdatedAt(data.updated_at);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.breadcrumb}>Home &nbsp;›&nbsp; Cancellation Policy</div>

        <h1 style={styles.title}>Cancellation Policy</h1>
        <p style={styles.intro}>
          You can cancel a booking anytime before it's completed. A cancellation charge
          may apply depending on how far along your booking is at the time you cancel —
          the later you cancel, the higher the charge, since the partner has already
          committed time or started work.
        </p>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Cancellation Charges</h2>
          <div style={styles.tierGrid}>
            {charges.map((tier) => {
              const Icon = TIER_ICON[tier.condition] || Info;
              return (
                <div key={tier.condition || tier.label} style={styles.tierCard}>
                  <div style={styles.tierIcon}>
                    <Icon size={20} color="#6d5efc" />
                  </div>
                  <div style={styles.tierPercent}>{tier.percent}%</div>
                  <div style={styles.tierLabel}>{tier.label}</div>
                  <div style={styles.tierNote}>of the total order value</div>
                </div>
              );
            })}
          </div>
          {updatedAt && (
            <div style={styles.updatedNote}>Charges last updated: {updatedAt}</div>
          )}
        </div>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>How Refunds Work</h2>
          <ul style={styles.list}>
            <li>The applicable cancellation charge is deducted from your order value; the remaining amount is refunded to your original payment method.</li>
            <li>If you paid via "Pay After Work", only the cancellation charge (if any) is payable — there is nothing to refund.</li>
            <li>Refunds are typically processed within 5–7 business days, depending on your bank or payment provider.</li>
          </ul>
        </div>

        <div style={styles.noteBar}>
          <Info size={16} color="#6d5efc" />
          <span>
            These charges are reviewed periodically and may change. The exact charge for
            your booking is always shown before you confirm a cancellation.
          </span>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "#f5f6fb",
    minHeight: "100vh",
    padding: "40px 16px",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#111827",
  },
  container: {
    maxWidth: "860px",
    margin: "0 auto",
  },
  breadcrumb: {
    color: "#7b8198",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "14px",
  },
  title: {
    margin: 0,
    fontSize: "32px",
    fontWeight: 800,
    color: "#1c2148",
    marginBottom: "12px",
  },
  intro: {
    margin: 0,
    fontSize: "15px",
    lineHeight: 1.75,
    color: "#5c6480",
    marginBottom: "28px",
    maxWidth: "700px",
  },
  card: {
    background: "#fff",
    borderRadius: "20px",
    padding: "26px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
    marginBottom: "20px",
  },
  cardTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 800,
    color: "#21284d",
    marginBottom: "18px",
  },
  tierGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "14px",
  },
  tierCard: {
    background: "#f8f8fe",
    borderRadius: "16px",
    padding: "18px",
    textAlign: "center",
  },
  tierIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "#f0eeff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 10px",
  },
  tierPercent: {
    fontSize: "26px",
    fontWeight: 800,
    color: "#1d2448",
    marginBottom: "4px",
  },
  tierLabel: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#3c4462",
    marginBottom: "4px",
  },
  tierNote: {
    fontSize: "12px",
    color: "#8791ac",
  },
  updatedNote: {
    marginTop: "16px",
    fontSize: "12px",
    color: "#9ca3af",
  },
  list: {
    margin: 0,
    paddingLeft: "20px",
    color: "#5c6480",
    fontSize: "14px",
    lineHeight: 1.9,
  },
  noteBar: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    background: "#f0eeff",
    borderRadius: "14px",
    padding: "16px 18px",
    fontSize: "13px",
    color: "#4a4470",
    lineHeight: 1.6,
  },
};

export default CancellationPolicy;
